import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { caseStudies } from '../data/mock';

const CaseStudies = () => {
  const [selectedStudy, setSelectedStudy] = useState(caseStudies[0]);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Real Results From
            <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Real Partnerships</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Explore how we've helped enterprises transform their operations and achieve measurable business outcomes.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 mb-12">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="manufacturing">Manufacturing</TabsTrigger>
              <TabsTrigger value="finance">Finance</TabsTrigger>
              <TabsTrigger value="retail">Retail</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-8">
              {caseStudies.map((study) => (
                <Card 
                  key={study.id}
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-200 cursor-pointer"
                  onClick={() => setSelectedStudy(study)}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3">
                    <div className="bg-gradient-to-br from-orange-100 to-gray-100 p-12 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl font-bold text-gray-900 mb-2">
                          {study.results[0].split('%')[0]}%
                        </div>
                        <p className="text-gray-600">{study.results[0].split('%')[1]}</p>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-2 p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                          {study.industry}
                        </span>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{study.title}</h3>
                      <p className="text-sm text-gray-500 mb-4">{study.client}</p>
                      
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                        <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
                      </div>
                      
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                        <p className="text-gray-600 leading-relaxed">{study.solution}</p>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        {study.results.map((result, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                            <CheckCircle2 className="w-4 h-4 text-orange-500" />
                            <span className="text-sm text-gray-700">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="manufacturing">
              <Card className="p-12">
                <p className="text-center text-gray-600">Filter showing manufacturing case studies...</p>
              </Card>
            </TabsContent>

            <TabsContent value="finance">
              <Card className="p-12">
                <p className="text-center text-gray-600">Filter showing financial services case studies...</p>
              </Card>
            </TabsContent>

            <TabsContent value="retail">
              <Card className="p-12">
                <p className="text-center text-gray-600">Filter showing retail case studies...</p>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Detailed Case Study */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Card className="overflow-hidden border-2 border-orange-100">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-12 text-white">
              <div className="max-w-4xl">
                <span className="px-4 py-2 bg-orange-400/20 text-orange-400 text-sm font-medium rounded-full">
                  Featured Case Study
                </span>
                <h2 className="text-4xl font-bold mt-6 mb-4">{selectedStudy.title}</h2>
                <p className="text-xl text-gray-300">{selectedStudy.client}</p>
              </div>
            </div>

            <div className="p-12 bg-gradient-to-br from-white to-gray-50">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <span className="text-orange-600 font-bold">1</span>
                      </div>
                      The Challenge
                    </h3>
                    <p className="text-gray-600 leading-relaxed pl-10">{selectedStudy.challenge}</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <span className="text-orange-600 font-bold">2</span>
                      </div>
                      Our Solution
                    </h3>
                    <p className="text-gray-600 leading-relaxed pl-10">{selectedStudy.solution}</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <span className="text-orange-600 font-bold">3</span>
                      </div>
                      Measurable Impact
                    </h3>
                    <ul className="space-y-3 pl-10">
                      {selectedStudy.results.map((result, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <TrendingUp className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <Card className="p-6 bg-white border-l-4 border-orange-400">
                    <h4 className="font-bold text-gray-900 mb-3">Industry</h4>
                    <p className="text-gray-600">{selectedStudy.industry}</p>
                  </Card>

                  <Card className="p-6 bg-white border-l-4 border-gray-400">
                    <h4 className="font-bold text-gray-900 mb-3">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {["AI/ML", "Cloud", "Integration", "Analytics"].map((tech) => (
                        <span key={tech} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </Card>

                  <Card className="p-6 bg-white border-l-4 border-orange-400">
                    <h4 className="font-bold text-gray-900 mb-3">Project Duration</h4>
                    <p className="text-gray-600">12 weeks from discovery to go-live</p>
                  </Card>

                  <Card className="p-6 bg-gradient-to-br from-orange-50 to-white">
                    <h4 className="font-bold text-gray-900 mb-3">Client Testimonial</h4>
                    <p className="text-gray-600 italic leading-relaxed">
                      "AiFlo.ai's AI-first approach and single accountability model made our digital transformation seamless. The results exceeded our expectations."
                    </p>
                  </Card>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss how we can help you achieve similar breakthrough results.
          </p>
          <Link to="/contact">
            <Button className="bg-orange-400 hover:bg-orange-500 text-white px-8 py-6 text-lg rounded-xl shadow-2xl">
              Start Your Transformation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
